import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    AutocompleteInput,
    BooleanInput,
    NumberInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TextInput,
    usePermissions,
    useRecordContext,
    useTranslate,
    email,
    maxLength,
    number,
    regex,
    required,
} from 'react-admin';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar';
import { countryChoices, forwarderTypeChoices, operatorCouponChoices, provinceChoices } from '@/components/global/options/selectOptions';
import { WorkingDayGroupInput } from '@/components/collectionPoint/WorkingDays';
import { CouponGroupInput } from '@/components/collectionPoint/CouponGroupInput';
import AddressInput from '@/components/collectionPoint/AddressInput.jsx';
import { OperatorPointIdInput, requiredOperators } from '@/components/collectionPoint/OperatorPointIdInput.jsx';
import { useFormContext, useWatch } from 'react-hook-form';
import { useContractor } from '@/composables/useContractor.js';
import { CustomRichText } from '@/components/global/inputs/CustomRichText';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';
import { validNip } from '@/components/global/validators/customValidators.jsx';

const StyledCollectionPointForm = styled(Box)(() => ({
    width: '100%',
    '& .MuiCardContent-root': {
        width: '100%',
        marginBottom: '34px',
    },
    '& .MuiTypography-root.MuiTypography-h6': {
        marginBottom: '32px',
    },
}));

const ContractorInput = () => {
    const translate = useTranslate();
    const { setValue } = useFormContext();
    return (
        <Box className='autocomplete-parent'>
            <ReferenceInput source='contractorId' reference='contractor' perPage={25}>
                <AutocompleteInput
                    label={translate('common.contractor')}
                    filterToQuery={searchText => ({
                        search: { value: searchText, fields: { name: true } },
                    })}
                    fullWidth
                    validate={required(translate('common.validation.required'))}
                    onChange={() => {
                        setValue('operator', null);
                        setValue('type', null);
                        setValue('serviceCompanyId', null);
                    }}
                    disablePortal={true}
                />
            </ReferenceInput>
        </Box>
    );
};

const OperatorInput = () => {
    const translate = useTranslate();
    const { contractorId, contractor, isLoading } = useContractor();
    const { setValue } = useFormContext();
    const choices = (contractor?.operators ?? []).map(item => ({ id: item, name: item }));
    const selectedCouponSource = useWatch({ name: 'couponSource' });

    const handleChange = value => {
        if (selectedCouponSource) {
            const matchingCouponSourceOption = operatorCouponChoices.find(choice => choice.id === selectedCouponSource.toString().toLowerCase());
            if (matchingCouponSourceOption) {
                setValue('couponSource', null);
            }
        }
        if (!requiredOperators.includes(value)) {
            setValue('operatorPointId', null);
        }
    };

    return (
        <SelectInput
            source='operator'
            label={translate('resources.collectionPoint.form.operatorForCollectionPoint')}
            fullWidth={true}
            choices={choices}
            isLoading={isLoading}
            disabled={!contractorId}
            emptyText={translate('common.all')}
            onChange={handleChange}
        />
    );
};

const PosTypeInput = () => {
    const translate = useTranslate();
    const { contractorId, contractor, isLoading } = useContractor();
    const choices = (contractor?.posTypes ?? []).map(item => ({ id: item, name: item }));
    return (
        <SelectInput
            source='type'
            label={translate('resources.collectionPoint.form.posType')}
            fullWidth={true}
            choices={choices}
            isLoading={isLoading}
            disabled={!contractorId}
            emptyText={translate('common.all')}
        />
    );
};

const CollectionPointNipInput = () => {
    const translate = useTranslate();
    const { contractor, isLoading } = useContractor();
    const contractorNip = contractor?.nip;
    const placeholder = contractorNip
        ? translate('resources.collectionPoint.form.nipFromContractor', { nip: contractorNip })
        : translate('resources.collectionPoint.form.nip');

    return (
        <TextInput
            source='nip'
            label={translate('resources.collectionPoint.form.nip')}
            placeholder={placeholder}
            validate={validNip}
            fullWidth={true}
            isLoading={isLoading}
        />
    );
};

const CollectionPointForwarderInput = () => {
    const translate = useTranslate();
    const { contractor, isLoading } = useContractor();
    const contractorForwarderType = contractor?.forwarderType;
    const contractorForwarderText = forwarderTypeChoices(translate).find(choice => choice.id === contractorForwarderType)?.name || contractorForwarderType;

    return (
        <SelectInput
            source='forwarderType'
            label={translate('resources.collectionPoint.form.forwarderType')}
            fullWidth={true}
            choices={forwarderTypeChoices(translate)}
            isLoading={isLoading}
            emptyText={translate('resources.collectionPoint.labels.forwarderTypeFromContractor', { forwarderType: contractorForwarderText })}
        />
    );
};

const ServiceCompanyInput = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const { setValue } = useFormContext();
    const { contractorId, contractor, isLoading } = useContractor({
        onSuccess: data => {
            if (data.defaultServiceCompanyId && (record?.contractorId !== data.id || !record?.serviceCompanyId)) {
                setValue('serviceCompanyId', data.defaultServiceCompanyId, { shouldDirty: true });
            }
        },
    });
    const availableCompanies = contractor?.serviceCompanyIds ?? [];
    const validators = availableCompanies.length > 0 ? [required(translate('common.validation.required'))] : [];

    return (
        <ReferenceInput
            source='serviceCompanyId'
            reference='service-company'
            filter={{ id_in: availableCompanies }}
            enableGetChoices={({ id_in }) => id_in.length > 0}
        >
            <SelectInput
                label={translate('resources.collectionPoint.form.serviceCompany')}
                fullWidth
                isLoading={isLoading}
                disabled={!contractorId || availableCompanies.length === 0}
                validate={validators}
                emptyText={translate('common.all')}
            />
        </ReferenceInput>
    );
};

export const CollectionPointForm = ({ handleClose }) => {
    const translate = useTranslate();
    const { isLoading, permissions } = usePermissions();
    const record = useRecordContext();
    if (isLoading) return null;

    const isAdmin = permissions?.isAdmin;
    const isEditMode = !!record?.id;
    const canEditServiceNote = isEditMode ? isAdmin || permissions?.isServiceMaster : isAdmin;
    const validatePhone = regex(/^\+48[1-9]\d{8}$/, translate('resources.user.validation.invalidPhone'));

    return (
        <StyledCollectionPointForm>
            <SimpleForm
                toolbar={<TagCategoryToolbar onCancel={handleClose} cancelLabel={translate('common.cancel')} saveLabel={translate('common.save')} />}
                mode='onSubmit'
            >
                <Box sx={{ width: '100%' }} className='pos-form-content'>
                    <TextInput
                        source='customId'
                        label={translate('resources.collectionPoint.form.collectionPointNameId')}
                        validate={[required(translate('common.validation.required')), maxLength(150, translate('common.validation.maxLength', { count: 150 }))]}
                        fullWidth
                    />
                    {isAdmin && <ContractorInput />}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '12px',
                            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                flexDirection: 'column',
                                gap: '0px',
                            },
                        }}
                    >
                        <Box flex={1}>
                            <TextInput
                                source='shopLabel'
                                label={translate('resources.collectionPoint.form.label')}
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(32, translate('common.validation.maxLength', { count: 32 })),
                                ]}
                                fullWidth
                            />
                        </Box>
                        <Box flex={1}>
                            <TextInput
                                source='shopName'
                                label={translate('resources.collectionPoint.fields.shopName')}
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(32, translate('common.validation.maxLength', { count: 32 })),
                                ]}
                                fullWidth
                            />
                        </Box>
                    </Box>
                    <PosTypeInput />
                    {isAdmin && <CollectionPointNipInput />}
                    {isAdmin && <CollectionPointForwarderInput />}
                    <OperatorInput />
                    <OperatorPointIdInput />
                    <CouponGroupInput />
                    {!permissions?.isService && <ServiceCompanyInput />}
                    {canEditServiceNote && (
                        <Box
                            sx={{
                                '& .rich-text-input': {
                                    '& .MuiToggleButtonGroup-root:nth-of-type(1)': {
                                        bottom: 40,
                                        [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                                            bottom: 90,
                                        },
                                    },
                                    '& .MuiToggleButtonGroup-root:nth-of-type(2)': {
                                        bottom: 40,
                                        [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                                            bottom: 44,
                                        },
                                    },
                                    '& .RaLabeled-label': {
                                        display: 'block',
                                        position: 'absolute',
                                        zIndex: 2,
                                        backgroundColor: Palette.white,
                                        padding: '0 5px',
                                        top: '-8px',
                                        left: '10px',
                                        fontSize: '11px',
                                    },
                                    '& .RaRichTextInput-editorContent': {
                                        '& .tiptap': {
                                            borderRadius: '12px',
                                        },
                                    },
                                },
                            }}
                        >
                            <CustomRichText
                                source='serviceNote'
                                label={translate('resources.collectionPoint.fields.serviceNote')}
                                fullWidth
                                validate={maxLength(1000, translate('common.validation.maxLength', { count: 1000 }))}
                            />
                        </Box>
                    )}
                    <Typography variant='h6' gutterBottom>
                        {translate('common.address')}
                    </Typography>
                    <AddressInput sx={{ width: '100%' }} />
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '12px',
                            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                flexDirection: 'column',
                                gap: '0px',
                            },
                        }}
                    >
                        <Box flex={1}>
                            <TextInput
                                source='address'
                                fullWidth
                                label={translate('resources.collectionPoint.fields.address')}
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                                    regex(/\d/, translate('common.validation.validAddress')),
                                ]}
                                readOnly
                            />
                        </Box>
                        <Box flex={1}>
                            <TextInput source='city' label={translate('common.city')} fullWidth readOnly />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '12px',
                            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                flexDirection: 'column',
                                gap: '0px',
                            },
                        }}
                    >
                        <Box flex={1}>
                            <TextInput source='postalCode' label={translate('common.postalCode')} fullWidth readOnly />
                        </Box>
                        <Box flex={1}>
                            <SelectInput
                                choices={provinceChoices(translate)}
                                source='province'
                                label={translate('resources.collectionPoint.form.voivodeship')}
                                fullWidth
                                readOnly
                                emptyText={translate('common.all')}
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '12px',
                            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                flexDirection: 'column',
                                gap: '0px',
                            },
                        }}
                    >
                        <Box flex={1}>
                            <SelectInput
                                choices={countryChoices(translate)}
                                defaultValue='Polska'
                                source='country'
                                fullWidth
                                label={translate('resources.collectionPoint.form.country')}
                                emptyText={translate('common.all')}
                                readOnly
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '12px',
                            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                flexDirection: 'column',
                                gap: '0px',
                            },
                        }}
                    >
                        <Box flex={1}>
                            <NumberInput source='latitude' label={translate('resources.collectionPoint.form.latitude')} fullWidth validate={number()} />
                        </Box>
                        <Box flex={1}>
                            <NumberInput source='longitude' label={translate('resources.collectionPoint.form.longitude')} fullWidth validate={number()} />
                        </Box>
                    </Box>
                    <Box display='flex'>
                        <Box flex={1}>
                            <TextInput
                                source='receiptAddress'
                                label={translate('resources.collectionPoint.form.receiptAddress')}
                                fullWidth
                                validate={maxLength(100, translate('common.validation.maxLength', { count: 100 }))}
                            />
                        </Box>
                    </Box>
                    <Typography variant='h6' gutterBottom>
                        {translate('resources.collectionPoint.form.contactPerson')}
                    </Typography>
                    <TextInput
                        source='contactName'
                        label={translate('resources.user.labels.userInfoTitle')}
                        fullWidth
                        validate={[required(translate('common.validation.required')), maxLength(150, translate('common.validation.maxLength', { count: 150 }))]}
                    />
                    <TextInput
                        source='contactEmail'
                        fullWidth
                        label={translate('resources.collectionPoint.form.contactEmail')}
                        validate={[
                            required(translate('common.validation.required')),
                            maxLength(100, translate('common.validation.maxLength', { count: 100 })),
                            email(),
                        ]}
                    />
                    <TextInput
                        source='contactPhoneNumber'
                        fullWidth
                        label={translate('resources.collectionPoint.fields.ownerPhoneNumber')}
                        placeholder='+48XXXXXXXXX'
                        validate={[required(translate('common.validation.required')), validatePhone]}
                    />
                    <TextInput
                        source='employeePhoneNumber'
                        fullWidth
                        label={translate('resources.collectionPoint.fields.employeePhoneNumber')}
                        placeholder='+48XXXXXXXXX'
                        validate={validatePhone}
                    />
                    <Typography variant='h6' gutterBottom>
                        {translate('resources.collectionPoint.form.workingHours')}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            marginBottom: '32px',
                        }}
                    >
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.mon')} dayShortName='mon' />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.tue')} dayShortName='tue' />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.wed')} dayShortName='wed' />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.thu')} dayShortName='thu' />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.fri')} dayShortName='fri' />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.sat')} dayShortName='sat' />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.sun')} dayShortName='sun' />
                    </Box>
                    {isAdmin && <BooleanInput source='premiumService' size='small' fullWidth sx={{ '& .MuiFormControlLabel-root': { marginLeft: '0' } }} />}
                </Box>
            </SimpleForm>
        </StyledCollectionPointForm>
    );
};

export default CollectionPointForm;
